import React from 'react';
import {
  EnvelopeIcon,
  UserGroupIcon,
  TableCellsIcon,
  EyeIcon,
  ClockIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';

export const SmsCampaignGuidePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <EnvelopeIcon className="w-8 h-8 text-[#FF5722]" />
              <h1 className="text-3xl font-bold text-gray-900">
                How to Create an SMS Campaign
              </h1>
            </div>
            <p className="mt-2 text-sm text-gray-600 max-w-2xl">
              Use this step‑by‑step guide to build, preview, schedule and send an SMS Campaign using
              the tools already available on the platform.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Overview steps */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Step 1
            </h2>
            <p className="text-base font-medium text-gray-900">
              Open the SMS Campaign page
            </p>
            <p className="mt-2 text-sm text-gray-600">
              From the main navigation, click <span className="font-medium">SMS Campaign</span> to
              start a new campaign.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Step 2
            </h2>
            <p className="text-base font-medium text-gray-900">
              Choose campaign details & sender
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Name your campaign, optionally schedule it, and select the Sender ID to send from.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Step 3
            </h2>
            <p className="text-base font-medium text-gray-900">
              Select your audience
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Add recipients from contacts, imports, or groups. The system validates all numbers.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Step 4
            </h2>
            <p className="text-base font-medium text-gray-900">
              Write, preview & send
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Compose your message, personalize it with variables, preview the campaign, then send or
              schedule.
            </p>
          </div>
        </div>

        {/* Step 1 & 2: basic setup */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <EnvelopeIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">1. Open SMS Campaign</h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>
                After logging in, use the left navigation to click{' '}
                <span className="font-medium">SMS Campaign</span>.
              </li>
              <li>
                You&apos;ll see the campaign builder with sections for campaign details, audience,
                message content, and AI assistant.
              </li>
              <li>
                At the bottom of the page you&apos;ll find{' '}
                <span className="font-medium">Preview SMS</span> and{' '}
                <span className="font-medium">Send Campaign</span> actions.
              </li>
            </ol>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <ClockIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">
                2. Set campaign name, schedule & sender
              </h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>
                In <span className="font-medium">Campaign Name</span>, enter a clear title (for
                example: &quot;July Promo – Data Bundles&quot;). This appears in logs and summaries.
              </li>
              <li>
                If you want to send later, pick a{' '}
                <span className="font-medium">Schedule Date</span> and{' '}
                <span className="font-medium">Schedule Time</span>. Leave them empty to send
                immediately.
              </li>
              <li>
                Under <span className="font-medium">Select Sender I.D.</span>, choose one of the
                approved Sender IDs retrieved from the backend. If none are available, contact your
                administrator to configure one.
              </li>
            </ol>
            <p className="mt-4 text-xs text-gray-500">
              Note: Sender IDs are managed centrally and may require approval before use.
            </p>
          </div>
        </div>

        {/* Step 3: audience selection */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <UserGroupIcon className="w-7 h-7 text-[#FF5722]" />
            <h3 className="text-lg font-semibold text-gray-900">3. Choose your audience</h3>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            Use the audience tiles to pull recipients from different sources. All numbers are
            validated before being added to the campaign.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-900 mb-1">Add Contact</p>
              <p className="text-xs text-gray-600">
                Manually add one or more phone numbers using the Add Contacts modal.
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-900 mb-1">Import Contacts</p>
              <p className="text-xs text-gray-600">
                Import recipients from a CSV or Excel file using the Import Contacts modal.
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-900 mb-1">All Contacts</p>
              <p className="text-xs text-gray-600">
                Select existing contacts that are already stored in the platform.
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-900 mb-1">Groups</p>
              <p className="text-xs text-gray-600">
                Quickly target entire groups (for example: Customers, Staff, VIPs).
              </p>
            </div>
          </div>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li>
              Click one of the <span className="font-medium">Choose your audience</span> tiles (Add
              Contact, Import Contacts, All Contacts, or Group).
            </li>
            <li>
              In the modal that opens, select or import the contacts you want to include. On import,
              the system uses the backend validation logic to:
              <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                <li>clean numbers (removing spaces and punctuation)</li>
                <li>
                  ensure each number starts with <span className="font-mono">263</span> and has
                  exactly 12 digits (for example: <span className="font-mono">263712345678</span>)
                </li>
                <li>skip duplicates so each recipient only appears once</li>
              </ul>
            </li>
            <li>
              Confirm the selection to add recipients to the campaign&apos;s{' '}
              <span className="font-medium">Recipients</span> list.
            </li>
            <li>
              Review the <span className="font-medium">Recipients</span> box: it shows up to 5 phone
              numbers and a total count. If more than 5 are present, a note indicates how many extra
              recipients are included.
            </li>
          </ol>
          <p className="mt-4 text-xs text-gray-500">
            If any invalid numbers are found, the platform alerts you and does not add them to the
            campaign.
          </p>
        </div>

        {/* Step 4: compose message */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <TableCellsIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">4. Write your SMS</h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>
                Under <span className="font-medium">Add SMS message</span>, optionally pick an
                existing <span className="font-medium">Template</span> from the dropdown to reuse
                saved content.
              </li>
              <li>
                Type or edit your campaign message in the large text area. Keep it clear and concise
                to minimize SMS segments.
              </li>
              <li>
                Use the <span className="font-medium">Message Variables</span> dropdown to insert
                placeholders like <span className="font-mono">{'{NAME}'}</span>,{' '}
                <span className="font-mono">{'{PHONE}'}</span>, or{' '}
                <span className="font-mono">{'{UNSUBSCRIBE}'}</span> for personalization and
                compliance.
              </li>
              <li>
                Watch the <span className="font-medium">Total symbols</span> panel, which shows how
                many characters you&apos;ve used and how many SMS segments (
                <span className="font-mono">160</span> characters per segment) the message will
                consume.
              </li>
            </ol>
            <p className="mt-4 text-xs text-gray-500">
              The campaign summary below the form multiplies your SMS segments by the number of
              recipients to estimate total credit usage.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <EyeIcon className="w-7 h-7 text-[#FF5722]" />
              <h3 className="text-lg font-semibold text-gray-900">
                5. (Optional) Use the AI SMS Assistant
              </h3>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              The AI SMS Assistant can generate a draft message based on your goals and tone.
            </p>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>
                In the <span className="font-medium">AI SMS Assistant</span> panel, describe what you
                want to communicate in <span className="font-medium">Message Overview</span>.
              </li>
              <li>
                Choose a <span className="font-medium">Message Goal</span> (Promotional, Informational,
                Event Reminder, or Customer Service) and a <span className="font-medium">Tone</span>{' '}
                (Friendly, Professional, Casual, or Formal).
              </li>
              <li>
                Optionally, select variables to personalize the message. The assistant includes these
                in the generated content.
              </li>
              <li>
                Click <span className="font-medium">Generate Message</span>. The system calls the
                backend, then fills in an SMS body you can review and edit before sending.
              </li>
            </ol>
            <p className="mt-4 text-xs text-gray-500">
              AI output is just a starting point—always review for accuracy and local compliance
              before sending.
            </p>
          </div>
        </div>

        {/* Step 6: preview and send */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <PaperAirplaneIcon className="w-7 h-7 text-[#FF5722]" />
            <h3 className="text-lg font-semibold text-gray-900">
              6. Preview, confirm and send your campaign
            </h3>
          </div>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li>
              Review the <span className="font-medium">Campaign Summary</span> section: it shows your
              campaign name, total recipients, schedule, and estimated total credit usage.
            </li>
            <li>
              Click <span className="font-medium">Preview Campaign</span> at the bottom of the message
              area. This takes you to the <span className="font-medium">SMS Preview</span> page with:
              <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                <li>a sample of up to 5 recipient numbers</li>
                <li>the full &quot;Total Recipients&quot; count from the backend</li>
                <li>the exact message that will be sent</li>
              </ul>
            </li>
            <li>
              If anything looks wrong, use the <span className="font-medium">Back/Edit</span> option
              on the preview page to return and adjust your campaign.
            </li>
            <li>
              When you&apos;re satisfied, click{' '}
              <span className="font-medium">Send Campaign</span>. The frontend calls the backend
              `sendCampaignSms` API, which:
              <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                <li>queues the campaign for delivery (or schedules it for later)</li>
                <li>returns counts of queued, sent and invalid numbers</li>
                <li>logs the campaign and messages so you can view them in SMS Logs and dashboards</li>
              </ul>
            </li>
            <li>
              Watch the confirmation alert for a summary of{' '}
              <span className="font-medium">Queued</span>, <span className="font-medium">Sent</span>{' '}
              and <span className="font-medium">Invalid</span> recipients.
            </li>
          </ol>
          <p className="mt-4 text-xs text-gray-500">
            After sending, you can track performance from the dashboard and SMS Logs pages, which are
            powered by the same backend activity APIs described in the README.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SmsCampaignGuidePage;


